import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useEmploymentTypesSave } from "@/queries/employment-types.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmploymentType } from "@/types/employment-types.types.ts";
import { Button, Input, useModals, Modal, ModalProps, Stack } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface EmploymentTypeEditModelProps extends ModalProps {
  employmentType: ApiEmploymentType | undefined;
}

export function EmploymentTypeEditModal({
  employmentType,
  ...props
}: EmploymentTypeEditModelProps) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close } = useModals();

  const EmploymentTypeSchemaObject = z.object({
    title: z.string().min(1, t("errors:field_required")),
  });
  type EmploymentTypeSchema = z.infer<typeof EmploymentTypeSchemaObject>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EmploymentTypeSchema>({
    mode: "onSubmit",
    resolver: zodResolver(EmploymentTypeSchemaObject),
    values: {
      title: employmentType?.title ?? "",
    },
    defaultValues: {
      title: "",
    },
  });

  const employmentTypesSave = useEmploymentTypesSave(control);

  const onSubmit = handleSubmit(async (schema) => {
    try {
      const data = await employmentTypesSave.mutateAsync({
        id: employmentType?.id,
        payload: { title: schema.title },
      });

      if (employmentType?.id) {
        analyticsService.trackEvent(analyticEvents.employmentTypes.edited, {
          [analyticProperties.id]: employmentType.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.employmentTypes.created, {
          [analyticProperties.id]: data.id,
        });
      }

      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={t("core:employment_type")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={employmentTypesSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("common:title")}
          required={true}
          type={"text"}
          autoFocus={true}
          {...register("title")}
          error={errors.title?.message}
        />
      </form>
    </Modal>
  );
}
